import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../services/api';

/**
 * Hooks do modulo Financeiro (dados Conta Azul via /api/v1/financeiro/*).
 * Backend: especialistas-backend, tabela ca_lancamentos_financeiros.
 * Envelope padrao: { data: <T>, message, meta } -> desembrulha com res.data.data.
 * Paginado: data = { items, page, size, total, pages, next_page, previous_page }.
 */

interface IApiEnvelope<T> {
  data: T;
  message?: string;
  meta?: unknown;
}

export type TipoLancamento = 'RECEITA' | 'DESPESA';

export interface ILancamentoFinanceiro {
  id: string;
  tipo: TipoLancamento | null;
  pessoa_nome: string | null;
  documento: string | null;
  descricao: string | null;
  status: string | null;
  status_traduzido: string | null;
  total: number | null;
  pago: number | null;
  nao_pago: number | null;
  data_competencia: string | null;
  data_vencimento: string | null;
  data_pagamento?: string | null; // data real de baixa (modo caixa)
  categoria: string | null;
  centro_custo: string | null;
  external_id: number | null;
}

export interface IKpiValor {
  valor: number;
  qtd: number;
}

export interface IFinanceiroKpis {
  total: IKpiValor;
  pago: IKpiValor;
  em_aberto: IKpiValor;
  vencido: IKpiValor;
  perdido: IKpiValor;
}

export interface IAgingFaixa {
  faixa: 'A_VENCER' | 'D_1_30' | 'D_31_60' | 'D_61_90' | 'D_90_MAIS' | string;
  valor: number;
  qtd: number;
}

export interface IDreLinha {
  tipo: string;
  categoria: string;
  total: number;
  pago: number;
  nao_pago: number;
  qtd: number;
}

export interface IInadimplente {
  pessoa_nome: string | null;
  categoria: string | null;
  total: number;
  nao_pago: number;
  data_vencimento: string | null;
}

export interface IComposicao {
  categoria: string;
  valor: number;
}

export interface IFinanceiroDashboard {
  receber: IFinanceiroKpis;
  pagar: IFinanceiroKpis;
  saldo_previsto: number;
  aging_receber: IAgingFaixa[];
  dre: IDreLinha[];
  top_inadimplentes: IInadimplente[];
  composicao_receber: IComposicao[];
  composicao_pagar: IComposicao[];
}

export interface IFinanceiroDre {
  linhas: IDreLinha[];
  receita_total: number;
  despesa_total: number;
  resultado: number;
}

export interface IFinanceiroInadimplencia {
  aging: IAgingFaixa[];
  total: number;
  qtd: number;
  qtd_clientes: number;
  clientes: IInadimplente[];
}

export interface IFluxoMes {
  mes: string;
  entradas: number;
  saidas: number;
  resultado: number;
}

export interface ISaldoConta {
  id: string | null;
  nome: string | null;
  tipo: string | null;
  saldo: number;
  /** Limite (cheque especial) — manual, editável no admin. */
  limite: number;
  /** saldo + limite: a folga real que a diretoria vigia. */
  saldo_mais_limite: number;
  conta_origem: string | null;
  banco: string | null;
  agencia: string | null;
  numero: string | null;
}

export interface IFinanceiroFluxo {
  meses: IFluxoMes[];
  saldo_contas: ISaldoConta[];
  saldo_total: number;
}

export interface IDiretoriaFranquia {
  conta_origem: string | null;
  entradas: number;
  saidas: number;
  /** Subconjunto das saídas: pró-labore/retirada de sócio. */
  retirada_socios: number;
}

export interface IDiretoriaDia {
  dia: string; // 'YYYY-MM-DD'
  entradas: number;
  saidas: number;
}

export interface IFinanceiroDiretoria {
  por_franquia: IDiretoriaFranquia[];
  por_dia: IDiretoriaDia[];
  retirada_total: number;
}

export interface IAuditoriaCheck {
  chave: string;
  titulo: string;
  descricao: string;
  severidade: string; // alta | media | info
  qtd: number; // no recorte atual (período+franquia+busca)
  qtd_historico?: number; // mesmo recorte SEM o período
  dimensao_data?: 'competencia' | 'baixa';
  aplica_q?: boolean; // false: busca por entidade não alcança o check
}

export interface IPontualidadeCliente {
  cliente: string | null;
  titulos: number;
  atrasos: number;
  atraso_medio: number;
  atraso_max: number;
}

export interface IAuditoriaPontualidade {
  pct_em_dia: number;
  atraso_medio: number;
  qtd_atrasadores: number;
  ranking: IPontualidadeCliente[];
}

export interface IFinanceiroAuditoria {
  checks: IAuditoriaCheck[];
  pontualidade: IAuditoriaPontualidade;
  universo?: {
    lancamentos_periodo: number;
    baixas_periodo: number;
    titulos_com_ocorrencia: number;
  };
}

export interface IAuditoriaItem {
  id: string | null;
  tipo: string | null;
  pessoa_nome: string | null;
  conta_origem: string | null;
  total: number | null;
  pago: number | null;
  nao_pago: number | null;
  data_vencimento: string | null;
  data_pagamento: string | null;
  detalhe: string | null;
}

export interface IAuditoriaDrill {
  chave: string;
  titulo: string;
  total_ocorrencias: number;
  total_real?: number; // total no recorte, sem o cap de 300
  itens: IAuditoriaItem[];
}

export interface ICustoPessoalItem {
  categoria: string;
  grupo: string;
  valor: number;
}

export interface IFinanceiroCustoPessoal {
  itens: ICustoPessoalItem[];
  receita_total: number;
}

export interface ILancamentoPage {
  items: ILancamentoFinanceiro[];
  page: number;
  size: number;
  total: number;
  pages: number;
  next_page: number | null;
  previous_page: number | null;
}

export interface IFinanceiroBaseParams {
  external_id?: number;
  base?: 'vencimento' | 'competencia' | 'caixa';
  date_start?: string;
  date_end?: string;
  /** Franquia(s) de origem (conta_origem), separadas por virgula. Vazio = consolidado. */
  conta_origem?: string;
  /** Busca por pessoa_nome/descrição (entidade selecionada na ContextBar). */
  q?: string;
  /** Filtro por categoria (rubrica do Conta Azul). */
  categoria?: string;
  /** Filtro por centro de custo / unidade. */
  centro_custo?: string;
}

export interface IFinanceiroFranquias {
  franquias: string[];
}

export interface IFinanceiroFacets {
  categorias: string[];
  centros_custo: string[];
  status: string[];
}

export interface IFolhaColaborador {
  nome: string | null;
  cargo: string | null;
  departamento: string | null;
  franquia: string | null;
  salario: number | null;
  status: string | null;
  admissao: string | null;
  sexo: string | null;
}

export interface IFolhaDepartamento {
  departamento: string;
  qtd: number;
  salario_total: number;
}

export interface IFolhaFilial {
  franquia: string;
  qtd: number;
  salario_total: number;
}

export interface IFinanceiroFolha {
  colaboradores: IFolhaColaborador[];
  departamentos: IFolhaDepartamento[];
  filiais: IFolhaFilial[];
  folha_total: number;
  qtd_ativos: number;
  qtd_total: number;
}

export interface IDreComposicaoItem {
  pessoa_nome: string | null;
  valor: number;
  qtd: number;
}

export interface IDreComposicao {
  tipo: string;
  categoria: string;
  total: number;
  itens: IDreComposicaoItem[];
}

export interface ICustoColaborador {
  pessoa_nome: string | null;
  departamento: string | null;
  cargo: string | null;
  valor: number;
}

export interface ICustoArea {
  departamento: string;
  valor: number;
  qtd_colaboradores: number;
}

export interface IFinanceiroCustoPessoalDetalhe {
  colaboradores: ICustoColaborador[];
  areas: ICustoArea[];
  total: number;
  receita_total: number;
  sem_vinculo: number;
}

export interface IComparativoFranquia {
  franquia: string;
  receita: number;
  despesa: number;
  resultado: number;
  margem: number;
  vencido: number;
  pessoal: number;
  pessoal_pct: number;
  qtd: number;
  clientes_ativos: number;
  clientes_novos: number;
  clientes_encerrados: number;
}

export interface IFinanceiroComparativoFranquias {
  itens: IComparativoFranquia[];
  total_receita: number;
  total_despesa: number;
  total_resultado: number;
  total_vencido: number;
  total_pessoal: number;
  total_clientes_ativos: number;
  total_clientes_novos: number;
  total_clientes_encerrados: number;
}

export interface IGapMes {
  mes: string;
  a_receber: number;
  a_pagar: number;
  gap: number;
}

export interface IFinanceiroGaps {
  meses: IGapMes[];
  total_receber: number;
  total_pagar: number;
}

export interface IPrevisaoMes {
  mes: string;
  entradas_previstas: number;
  saidas_previstas: number;
  liquido: number;
  acumulado: number;
}

export interface ISaudeMetrica {
  chave: string;
  label: string;
  score: number;
  detalhe: string;
}

export interface IFinanceiroSaude {
  score: number;
  faixa: string;
  metricas: ISaudeMetrica[];
  previsao: IPrevisaoMes[];
}

export interface IVendaItem {
  id: string;
  data: string | null;
  numero: number | null;
  cliente_nome: string | null;
  situacao: string | null;
  total: number;
}

export interface IFinanceiroVendas {
  itens: IVendaItem[];
  total_valor: number;
  qtd: number;
  ticket_medio: number;
}

export interface IClienteRank {
  cliente_nome: string | null;
  total: number;
  qtd: number;
}

export interface IFinanceiroClientesRanking {
  itens: IClienteRank[];
  total_valor: number;
}

export interface IContratoItem {
  id: string;
  cliente_nome: string | null;
  status: string | null;
  total: number;
  proximo_vencimento: string | null;
}

export interface IFinanceiroContratos {
  itens: IContratoItem[];
  mrr: number;
  total_contratos: number;
  ativos: number;
}

export interface IInsightAchado {
  chave: string;
  severidade: 'bom' | 'info' | 'atencao' | 'critico' | string;
  titulo: string;
  detalhe: string;
}

export interface IFinanceiroInsights {
  resumo: string;
  achados: IInsightAchado[];
  /** Quando o snapshot foi gerado (servido do banco, não recomputa por acesso). */
  gerado_em?: string | null;
  /** Quando o botão Atualizar libera de novo (cooldown de 5h no servidor). */
  atualizacao_liberada_em?: string | null;
}

export interface IPessoaBusca {
  pessoa_nome: string | null;
  documento: string | null;
  external_id: number | null;
  tipo: 'cliente' | 'fornecedor' | string;
  total_receita: number;
  total_despesa: number;
  em_aberto: number;
  qtd: number;
  ultima_data: string | null;
}

export interface IFinanceiroPessoas {
  itens: IPessoaBusca[];
}

export interface IFinanceiroFiltro extends IFinanceiroBaseParams {
  tipo: TipoLancamento;
  status?: string;
  q?: string;
  /** Filtro por pessoa_nome EXATA (drill de composição → lançamentos). */
  pessoa_nome?: string;
  categoria?: string;
  centro_custo?: string;
  order?: string;
  order_by?: 'asc' | 'desc';
  page: number;
  size: number;
}

const limparParams = (obj: object) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  );

// ── Dashboard ─────────────────────────────────────────────────────────────
export const useFinanceiroDashboard = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'dashboard', params] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroDashboard> => {
      const res = await api.get<IApiEnvelope<IFinanceiroDashboard>>(
        '/api/v1/financeiro/dashboard',
        { params: limparParams(params) },
      );
      return res.data.data;
    },
  });

// ── KPIs (Receber/Pagar) ──────────────────────────────────────────────────
export const useFinanceiroKpis = (
  params: IFinanceiroBaseParams & { tipo: TipoLancamento; q?: string },
) =>
  useQuery({
    queryKey: ['financeiro', 'kpis', params] as const,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IFinanceiroKpis> => {
      const res = await api.get<IApiEnvelope<IFinanceiroKpis>>('/api/v1/financeiro/kpis', {
        params: limparParams(params),
      });
      return res.data.data;
    },
  });

// ── DRE / Resultado ───────────────────────────────────────────────────────
export const useFinanceiroDre = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'dre', params] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroDre> => {
      const res = await api.get<IApiEnvelope<IFinanceiroDre>>('/api/v1/financeiro/dre', {
        params: limparParams(params),
      });
      return res.data.data;
    },
  });

// ── Inadimplência de honorários ───────────────────────────────────────────
export const useFinanceiroInadimplencia = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'inadimplencia', params] as const,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IFinanceiroInadimplencia> => {
      const res = await api.get<IApiEnvelope<IFinanceiroInadimplencia>>(
        '/api/v1/financeiro/inadimplencia',
        { params: limparParams(params) },
      );
      return res.data.data;
    },
  });

// ── Fluxo de caixa ────────────────────────────────────────────────────────
export const useFinanceiroFluxo = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'fluxo', params] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroFluxo> => {
      const res = await api.get<IApiEnvelope<IFinanceiroFluxo>>('/api/v1/financeiro/fluxo', {
        params: limparParams(params),
      });
      return res.data.data;
    },
  });

export const useFinanceiroDiretoria = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'diretoria', params] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroDiretoria> => {
      const res = await api.get<IApiEnvelope<IFinanceiroDiretoria>>(
        '/api/v1/financeiro/diretoria',
        { params: limparParams(params) },
      );
      return res.data.data;
    },
  });

export const useFinanceiroAuditoria = (
  params: IFinanceiroBaseParams,
  escopo: 'periodo' | 'historico' = 'periodo',
) =>
  useQuery({
    queryKey: ['financeiro', 'auditoria', params, escopo] as const,
    staleTime: 5 * 60 * 1000,
    placeholderData: prev => prev,
    queryFn: async (): Promise<IFinanceiroAuditoria> => {
      const res = await api.get<IApiEnvelope<IFinanceiroAuditoria>>(
        '/api/v1/financeiro/auditoria',
        { params: limparParams({ ...params, escopo }) },
      );
      return res.data.data;
    },
  });

export interface IAuditoriaDrillOpts {
  escopo?: 'periodo' | 'historico';
  q?: string; // busca DENTRO do drill (drill_q)
  order?: string;
  orderBy?: 'asc' | 'desc';
}

export const useFinanceiroAuditoriaDrill = (
  chave: string | null,
  params: IFinanceiroBaseParams,
  opts?: IAuditoriaDrillOpts,
) =>
  useQuery({
    queryKey: ['financeiro', 'auditoria', 'drill', chave, params, opts] as const,
    enabled: !!chave,
    staleTime: 5 * 60 * 1000,
    placeholderData: prev => prev,
    queryFn: async (): Promise<IAuditoriaDrill> => {
      const res = await api.get<IApiEnvelope<IAuditoriaDrill>>(
        `/api/v1/financeiro/auditoria/${chave}`,
        {
          params: limparParams({
            ...params,
            escopo: opts?.escopo ?? 'periodo',
            drill_q: opts?.q || undefined,
            order: opts?.order,
            order_by: opts?.order ? opts?.orderBy : undefined,
          }),
        },
      );
      return res.data.data;
    },
  });

// ── Limite (cheque especial) de uma conta — dado manual (backend ca_conta_limite) ─
export interface IContaLimite {
  conta_id: string;
  conta_nome: string | null;
  conta_origem: string | null;
  limite: number;
}

export const useSetContaLimite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      contaId,
      limite,
    }: {
      contaId: string;
      limite: number;
    }): Promise<IContaLimite> => {
      const res = await api.put<IApiEnvelope<IContaLimite>>(
        `/api/v1/financeiro/contas/${encodeURIComponent(contaId)}/limite`,
        { limite },
      );
      return res.data.data;
    },
    // Revalida o fluxo (saldo_contas traz limite + saldo+limite recalculados).
    onSuccess: () => qc.invalidateQueries({ queryKey: ['financeiro', 'fluxo'] }),
  });
};

// ── Custo de pessoal ──────────────────────────────────────────────────────
export const useFinanceiroCustoPessoal = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'custo-pessoal', params] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroCustoPessoal> => {
      const res = await api.get<IApiEnvelope<IFinanceiroCustoPessoal>>(
        '/api/v1/financeiro/custo-pessoal',
        { params: limparParams(params) },
      );
      return res.data.data;
    },
  });

// ── Listagem paginada (Receber/Pagar) ─────────────────────────────────────
export const useFinanceiroLancamentos = (filtro: IFinanceiroFiltro) =>
  useQuery({
    queryKey: ['financeiro', 'lancamentos', filtro] as const,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ILancamentoPage> => {
      const res = await api.get<IApiEnvelope<ILancamentoPage>>('/api/v1/financeiro/lancamentos', {
        params: limparParams(filtro),
      });
      return res.data.data;
    },
  });

// ── Franquias (conta_origem) para o seletor global ────────────────────────
export const useFinanceiroFranquias = () =>
  useQuery({
    queryKey: ['financeiro', 'franquias'] as const,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroFranquias> => {
      const res = await api.get<IApiEnvelope<IFinanceiroFranquias>>('/api/v1/financeiro/franquias');
      return res.data.data;
    },
  });

// ── Facets (categoria, centro de custo, status) p/ os filtros das listas ──
export const useFinanceiroFacets = (tipo: TipoLancamento, contaOrigem?: string) =>
  useQuery({
    queryKey: ['financeiro', 'facets', tipo, contaOrigem] as const,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroFacets> => {
      const res = await api.get<IApiEnvelope<IFinanceiroFacets>>('/api/v1/financeiro/facets', {
        params: { tipo, conta_origem: contaOrigem || undefined },
      });
      return res.data.data;
    },
  });

// ── Folha do escritório (Domínio): colaboradores com salário real ─────────
export const useFinanceiroFolha = (somenteAtivos = false) =>
  useQuery({
    queryKey: ['financeiro', 'folha', somenteAtivos] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroFolha> => {
      const res = await api.get<IApiEnvelope<IFinanceiroFolha>>('/api/v1/financeiro/folha', {
        params: limparParams({ somente_ativos: somenteAtivos || undefined }),
      });
      return res.data.data;
    },
  });

// ── DRE: composicao de uma linha (drill do modal interativo) ──────────────
export const useFinanceiroDreComposicao = (
  params: IFinanceiroBaseParams & { tipo: string; categoria: string },
  enabled: boolean,
) =>
  useQuery({
    queryKey: ['financeiro', 'dre-composicao', params] as const,
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IDreComposicao> => {
      const res = await api.get<IApiEnvelope<IDreComposicao>>('/api/v1/financeiro/dre/composicao', {
        params: limparParams(params),
      });
      return res.data.data;
    },
  });

// ── Custo de pessoal por colaborador / area (cruza Dominio) ───────────────
export const useFinanceiroCustoPessoalDetalhe = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'custo-pessoal-detalhe', params] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroCustoPessoalDetalhe> => {
      const res = await api.get<IApiEnvelope<IFinanceiroCustoPessoalDetalhe>>(
        '/api/v1/financeiro/custo-pessoal/detalhe',
        { params: limparParams(params) },
      );
      return res.data.data;
    },
  });

// ── Comparativo de franquias (multi-base) ─────────────────────────────────
export const useFinanceiroComparativoFranquias = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'comparativo-franquias', params] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroComparativoFranquias> => {
      const res = await api.get<IApiEnvelope<IFinanceiroComparativoFranquias>>(
        '/api/v1/financeiro/comparativo-franquias',
        { params: limparParams(params) },
      );
      return res.data.data;
    },
  });

// ── Gaps recebíveis × pagos ───────────────────────────────────────────────
export const useFinanceiroGaps = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'gaps', params] as const,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IFinanceiroGaps> => {
      const res = await api.get<IApiEnvelope<IFinanceiroGaps>>('/api/v1/financeiro/gaps', {
        params: limparParams(params),
      });
      return res.data.data;
    },
  });

// ── Saúde financeira + previsão ───────────────────────────────────────────
export const useFinanceiroSaude = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'saude', params] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroSaude> => {
      const res = await api.get<IApiEnvelope<IFinanceiroSaude>>('/api/v1/financeiro/saude', {
        params: limparParams(params),
      });
      return res.data.data;
    },
  });

// ── Comercial: Vendas / Ranking de clientes / Contratos ───────────────────
export const useFinanceiroVendas = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'vendas', params] as const,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IFinanceiroVendas> => {
      const res = await api.get<IApiEnvelope<IFinanceiroVendas>>('/api/v1/financeiro/vendas', {
        params: limparParams(params),
      });
      return res.data.data;
    },
  });

export const useFinanceiroClientesRanking = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'clientes-ranking', params] as const,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IFinanceiroClientesRanking> => {
      const res = await api.get<IApiEnvelope<IFinanceiroClientesRanking>>(
        '/api/v1/financeiro/clientes/ranking',
        { params: limparParams(params) },
      );
      return res.data.data;
    },
  });

export const useFinanceiroContratos = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'contratos', params] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroContratos> => {
      const res = await api.get<IApiEnvelope<IFinanceiroContratos>>(
        '/api/v1/financeiro/contratos',
        { params: limparParams(params) },
      );
      return res.data.data;
    },
  });

// ── Busca de pessoas (autocomplete do topbar: cliente/fornecedor) ─────────
export const useFinanceiroPessoas = (q: string, contaOrigem?: string) =>
  useQuery({
    queryKey: ['financeiro', 'pessoas', q, contaOrigem] as const,
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IFinanceiroPessoas> => {
      const res = await api.get<IApiEnvelope<IFinanceiroPessoas>>('/api/v1/financeiro/pessoas', {
        params: limparParams({ q, conta_origem: contaOrigem, limit: 8 }),
      });
      return res.data.data;
    },
  });

// ── Insights (snapshot persistido; atualização manual com cooldown de 5h) ─
export const useFinanceiroInsights = (params: IFinanceiroBaseParams) =>
  useQuery({
    queryKey: ['financeiro', 'insights', params] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IFinanceiroInsights> => {
      const res = await api.get<IApiEnvelope<IFinanceiroInsights>>('/api/v1/financeiro/insights', {
        params: limparParams(params),
      });
      return res.data.data;
    },
  });

/** Recomputa os insights da combinação de filtros. O servidor responde 429
 *  (com mensagem em `detail`) se a última atualização tem menos de 5h. */
export const useAtualizarFinanceiroInsights = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: IFinanceiroBaseParams): Promise<IFinanceiroInsights> => {
      const res = await api.post<IApiEnvelope<IFinanceiroInsights>>(
        '/api/v1/financeiro/insights/atualizar',
        undefined,
        { params: limparParams(params) },
      );
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['financeiro', 'insights'] }),
  });
};
